import { useState, useEffect, useCallback } from "react";
import {
  subscribeSyncStatus,
  getSyncStatus,
  triggerSync,
  type SyncStatus,
} from "@/lib/sync-manager";
import {
  getPendingCount,
  getPendingDailyLogsByProject,
  type OfflineDailyLog,
} from "@/lib/offline-storage";
import { useOnlineStatus } from "./useOnlineStatus";

/**
 * Hook to get the current sync status and trigger syncs
 */
export function useOfflineSync() {
  const isOnline = useOnlineStatus();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    pendingCount: 0,
  });

  // Subscribe to sync status changes
  useEffect(() => {
    // Get initial status
    getSyncStatus().then(setSyncStatus);

    // Subscribe to updates
    const unsubscribe = subscribeSyncStatus((status) => {
      setSyncStatus(status);
    });

    return unsubscribe;
  }, []);

  // Trigger sync manually
  const sync = useCallback(async () => {
    if (isOnline) {
      await triggerSync();
    }
  }, [isOnline]);

  return {
    ...syncStatus,
    isOnline,
    sync,
    hasPendingItems: syncStatus.pendingCount > 0,
  };
}

/**
 * Hook to get pending items count
 */
export function usePendingCount() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const updateCount = async () => {
      const pendingCount = await getPendingCount();
      setCount(pendingCount);
    };

    updateCount();

    // Subscribe to sync status to update count
    const unsubscribe = subscribeSyncStatus(async () => {
      await updateCount();
    });

    return unsubscribe;
  }, []);

  return count;
}

/**
 * Hook to get pending daily logs for a specific project
 */
export function usePendingDailyLogs(projectId: string) {
  const [pendingLogs, setPendingLogs] = useState<OfflineDailyLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPendingLogs = async () => {
      try {
        const logs = await getPendingDailyLogsByProject(projectId);
        setPendingLogs(logs);
      } catch (error) {
        console.error("Failed to load pending logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPendingLogs();

    // Subscribe to sync status to refresh pending logs
    const unsubscribe = subscribeSyncStatus(async () => {
      await loadPendingLogs();
    });

    return unsubscribe;
  }, [projectId]);

  return { pendingLogs, isLoading };
}
